import { useMemo, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { T } from '@/ui/tokens';
import { Spinner } from '@/ui/primitives/Spinner';
import { config } from '@/config';
import { useResetPassword } from './useAuth';

export function ResetPasswordScreen(props: { token?: string; path?: string }) {
  const queryToken = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);
  const routeToken = props.token || queryToken;
  const [tokenInput, setTokenInput] = useState('');
  const token = routeToken || tokenInput.trim();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const reset = useResetPassword();

  const mismatch = password.length > 0 && confirm.length > 0 && password !== confirm;
  const tokenValid = /^[A-Za-z0-9_-]{20,128}$/.test(token);
  const canSubmit = tokenValid && password.length >= 6 && confirm.length >= 6 && !mismatch && !reset.isPending;

  const submit = (e: Event) => {
    e.preventDefault();
    if (!canSubmit) return;
    reset.mutate({ token, newPassword: password });
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    background: T.surface, border: `1px solid ${T.line}`,
    color: T.text, fontFamily: T.font, fontSize: 14, outline: 'none',
    boxSizing: 'border-box' as const,
  };

  return (
    <div style={{
      background: T.bg, minHeight: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '32px 28px',
    }}>
      <form onSubmit={submit} style={{
        width: '100%', maxWidth: 360,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 8 }}>
            Reset password
          </div>
          <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5 }}>
            Choose a new password for your Boostly account.
          </div>
        </div>

        {!routeToken && (
          <input
            value={tokenInput}
            onInput={(e) => setTokenInput((e.target as HTMLInputElement).value.replace(/[^A-Za-z0-9_-]/g, ''))}
            placeholder="Paste reset token from the email"
            autoCapitalize="off"
            autoCorrect="off"
            spellcheck={false}
            maxLength={128}
            required
            style={inputStyle}
          />
        )}
        <input
          type="password"
          value={password}
          onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
          placeholder="New password"
          required
          minLength={6}
          style={inputStyle}
        />
        <input
          type="password"
          value={confirm}
          onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
          placeholder="Confirm new password"
          required
          minLength={6}
          style={inputStyle}
        />

        {!token && <div style={{ fontSize: 13, color: T.danger }}>Reset token is required.</div>}
        {!!token && !tokenValid && <div style={{ fontSize: 13, color: T.danger }}>Reset token must be numbers only, max 10 digits.</div>}
        {mismatch && <div style={{ fontSize: 13, color: T.danger }}>Passwords do not match.</div>}
        {reset.isError && (
          <div style={{ fontSize: 13, color: T.danger }}>
            {(reset.error as Error).message}
          </div>
        )}
        {reset.isSuccess && (
          <div style={{ fontSize: 13, color: T.ok }}>
            Password updated. You can now sign in.
          </div>
        )}

        <button type="submit" disabled={!canSubmit || reset.isSuccess} style={{
          padding: '12px', borderRadius: 12,
          background: T.accent, color: T.accentInk,
          border: 'none', fontSize: 14, fontWeight: 700,
          fontFamily: T.font, cursor: canSubmit ? 'pointer' : 'not-allowed',
          opacity: canSubmit && !reset.isSuccess ? 1 : 0.7,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          {reset.isPending && <Spinner size={14}/>}
          Update password
        </button>

        <button type="button" onClick={() => route(config.routes.login)} style={{
          padding: '12px', borderRadius: 12,
          background: 'transparent', color: T.text,
          border: `1px solid ${T.line2}`, fontSize: 13, fontWeight: 600,
          fontFamily: T.font, cursor: 'pointer',
        }}>
          Back to login
        </button>
      </form>
    </div>
  );
}
